/**
 * 📋 SDLC 6.5.0 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/pages/manager/ManagerProfilePage.tsx
 * PURPOSE: Màn Thông tin của tôi — SC-013-MGR-PROFILE (Release v0 WP-5)
 *          1 page render theo role + có/không ca: bản chính "Trưởng ca · có ca"
 *          (profile.html) + biến thể "Quản lý chi nhánh · không ca" (profile-bm.html)
 *          2026-09-16: bot-060 follow-up (GH#871) — toast Timemark chuyển từ
 *          `onSuccess` sang `onSubmitted` (hiện NGAY khi API success, không
 *          đợi bấm "Về trang chủ"). Xem docs/05-test/13-test-issues/bot-060.md.
 * SDLC FRAMEWORK VERSION: 6.5.0 (7-Pillar + 9 Mental Models + Long-Running Agent Protocol)
 * DATE: 2026-09-16
 * COMPLIANCE: SDLC 6.5.0 + NQH Brand Design System + Zero Mock Tolerance
 * PILLAR: P1 (Stage 04 - BUILD) + P6 (UI Components)
 * SPRINT GOVERNANCE: Rule 3: Zero-Mock Tolerance
 *
 * @business-context
 * - Domain: Manager — Hồ sơ cá nhân + tự chấm công VÀO/RA + đổi mật khẩu
 * - Impact: UX Critical — khối [2] Hồ sơ, [4] Chấm công của tôi, [6] Gần đây,
 *   popup [P] chọn phương thức → modal [C1] Selfie / [C2] GPS, modal [M] Đổi MK
 * - Stakeholders: Branch Managers, Shift Managers, Head Chefs
 *
 * @technical-architecture
 * - Layer: Presentation Layer (Page Component)
 * - Pattern: React + TanStack Query (useManagerProfile hooks) — mọi data từ API
 *   thật (attendance.py / employee.py / tenant_users.py), geolocation browser
 *   thật, loading skeleton + empty/error state + toast
 * - Dependencies: React Router, useManagerProfile, useAuthStore, NQH design tokens
 *
 * @cross-references
 * - Mockup chuẩn: docs/02-design/04-ui-ux-design/design-imports/ui-ux-nqhbot/v0/schedule/manager/profile.html
 * - Biến thể BM: .../manager/profile-bm.html
 * - Hooks: frontend/src/hooks/useManagerProfile.ts
 * - Backend: backend/app/api/v2/endpoints/attendance.py (+ GET /clock-config)
 * ===============================================================================
 */

import React, { useEffect, useMemo, useState } from "react";
import { useAuthStore } from "../../store/auth";
import {
  useChangePassword,
  useClockConfig,
  useManagerSelfProfile,
  useMySchedule,
  useSelfAttendanceHistory,
  useSelfAttendanceStatus,
  useSelfCheckIn,
  useSelfCheckOut,
} from "../../hooks/useManagerProfile";
import type { SelfAttendanceRecord } from "../../services/api/managerProfileApi";
import { TimemarkForm } from "../../components/TimemarkForm";
import styles from "./ManagerProfilePage.module.css";

// ── Inline SVG icons (Heroicons — copy nguyên path từ mockup, 0 emoji) ──────

const CloseIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} width={22} height={22} aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const CameraIcon: React.FC<{ size?: number; strokeWidth?: number }> = ({ size = 20, strokeWidth = 1.6 }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} width={size} height={size} aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M6.827 6.175A2.31 2.31 0 015.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 00-1.134-.175 2.31 2.31 0 01-1.64-1.055l-.822-1.316a2.192 2.192 0 00-1.736-1.039 48.774 48.774 0 00-5.232 0 2.192 2.192 0 00-1.736 1.039l-.821 1.316z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 12.75a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0zM18.75 10.5h.008v.008h-.008V10.5z" />
  </svg>
);

const GpsIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} width={20} height={20} aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
  </svg>
);

const ChevronRightIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} width={16} height={16} aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
  </svg>
);

const WarningIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} width={20} height={20} aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
  </svg>
);

/** ③ Chấm công thủ công (Timemark) — Heroicons "clock", cùng style inline SVG như các icon trên. */
const ClockIconTimemark: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} width={20} height={20} aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6l4 2" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

// ── Helpers ────────────────────────────────────────────────────────────────

const pad2 = (n: number) => String(n).padStart(2, "0");

/** Date → yyyy-mm-dd (local) */
const toISODate = (d: Date) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

/** Date → dd/mm/yyyy */
const formatDateVi = (d: Date) => `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;

/** Date → dd/mm */
const formatDayMonth = (d: Date) => `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}`;

/** ISO datetime → HH:MM (local) */
const formatTime = (iso: string | null) => {
  if (!iso) return "—:—";
  const d = new Date(iso);
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};

/** ISO datetime → dd/MM HH:mm (tiêu đề modal [C1]/[C2] + caption ảnh) */
const formatDayTime = (d: Date) =>
  `${formatDayMonth(d)} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;

/** Haversine (m) — đối chiếu vị trí browser với tọa độ chi nhánh (modal [C1]/[C2]) */
const haversineM = (lat1: number, lon1: number, lat2: number, lon2: number) => {
  const R = 6371000;
  const rad = (x: number) => (x * Math.PI) / 180;
  const a =
    Math.sin(rad(lat2 - lat1) / 2) ** 2 +
    Math.cos(rad(lat1)) * Math.cos(rad(lat2)) * Math.sin(rad(lon2 - lon1) / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

/** Nhãn vai trò VI verbatim mockup sidebar__org-role (đồng bộ ManagerLayout).
 * TENANT_OWNER: đổi "Chủ sở hữu"→"Chủ doanh nghiệp" (2026-08-28) khớp SSOT
 * `UI-LABELS-VI-ENUM.md` §5 — xem ghi chú đầy đủ tại ManagerLayout.tsx.
 * CORPORATE_MANAGER: đổi "Quản lý vùng"→"Quản lý phòng ban" cùng đợt (Drift #6). */
const ROLE_LABELS: Record<string, string> = {
  BRANCH_MANAGER: "Quản lý chi nhánh",
  SHIFT_MANAGER: "Trưởng ca",
  HEAD_CHEF: "Bếp trưởng",
  CORPORATE_MANAGER: "Quản lý phòng ban",
  TENANT_ADMIN: "Quản trị viên",
  TENANT_OWNER: "Chủ doanh nghiệp",
};

/** Trạng thái định vị trong modal [C1]/[C2] */
type GeoState =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "ok"; latitude: number; longitude: number; accuracy: number; at: Date };

type ActiveModal = "method" | "selfie" | "gps" | "timemark" | "pwd" | null;

type ClockDirection = "VÀO" | "RA";

// ── Component ──────────────────────────────────────────────────────────────

const ManagerProfilePage: React.FC = () => {
  const { user, userRole } = useAuthStore();
  const normalizedRole = userRole?.toUpperCase() || "";
  const roleLabel = ROLE_LABELS[normalizedRole] || normalizedRole || "—";
  // Quản lý chi nhánh: không xếp ca → ghi nhận có mặt tự do, auto "Đã ghi nhận"
  // (mockup profile-bm.html); Trưởng ca/Bếp trưởng: chờ BM duyệt (is_approved).
  const isAutoRecorded = normalizedRole === "BRANCH_MANAGER";

  const profileQuery = useManagerSelfProfile();
  const statusQuery = useSelfAttendanceStatus();
  const clockConfigQuery = useClockConfig();
  const historyQuery = useSelfAttendanceHistory();
  const myScheduleQuery = useMySchedule();

  const checkInMutation = useSelfCheckIn();
  const checkOutMutation = useSelfCheckOut();
  const changePasswordMutation = useChangePassword();

  const [activeModal, setActiveModal] = useState<ActiveModal>(null);
  const [geo, setGeo] = useState<GeoState>({ kind: "loading" });
  const [actionError, setActionError] = useState<string | null>(null);
  const [selfieNote, setSelfieNote] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  // Modal [M] fields
  const [pwdCurrent, setPwdCurrent] = useState("");
  const [pwdNew, setPwdNew] = useState("");
  const [pwdConfirm, setPwdConfirm] = useState("");
  const [pwdNewError, setPwdNewError] = useState(false);
  const [pwdConfirmError, setPwdConfirmError] = useState(false);
  const [pwdBanner, setPwdBanner] = useState<string | null>(null);

  const statusData = statusQuery.data;
  const clockState = statusData?.status ?? "NOT_CLOCKED_IN";
  const clockDir: ClockDirection = clockState === "CLOCKED_IN" ? "RA" : "VÀO";
  const showClockButton = clockState !== "CLOCKED_OUT";
  const todayShift = statusData?.today_shift ?? null;
  const clockConfig = clockConfigQuery.data;
  const gpsRadius = clockConfig?.gps_radius_meters ?? 100;

  // Toast tự ẩn sau 4s (pattern window.nqhToast trong mockup common.js)
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(timer);
  }, [toast]);

  // Lấy vị trí browser THẬT khi mở modal [C1]/[C2] (spinner "đang lấy vị trí")
  useEffect(() => {
    if (activeModal !== "selfie" && activeModal !== "gps") return;
    setGeo({ kind: "loading" });
    if (!navigator.geolocation) {
      setGeo({ kind: "error", message: "Trình duyệt không hỗ trợ định vị GPS" });
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) =>
        setGeo({
          kind: "ok",
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          at: new Date(),
        }),
      () =>
        setGeo({
          kind: "error",
          message: "Không lấy được vị trí GPS. Kiểm tra quyền định vị và thử lại",
        }),
      { enableHighAccuracy: true, timeout: 10000 },
    );
  }, [activeModal]);

  // Độ lệch GPS so với chi nhánh (chỉ tính khi có tọa độ chi nhánh từ API)
  const geoDistance = useMemo(() => {
    if (geo.kind !== "ok") return null;
    if (statusData?.branch_latitude == null || statusData?.branch_longitude == null) return null;
    return haversineM(
      geo.latitude,
      geo.longitude,
      statusData.branch_latitude,
      statusData.branch_longitude,
    );
  }, [geo, statusData]);

  // ── Handlers ──

  const closeModal = () => {
    setActiveModal(null);
    setActionError(null);
    setSelfieNote(null);
  };

  /** [5] Chấm công VÀO/RA → popup [P] chọn phương thức */
  const openMethodPicker = () => {
    setActionError(null);
    setActiveModal("method");
  };

  /** ① → [C1] Selfie · ② → [C2] Định vị GPS · [‹ Đổi phương thức] → về [P] */
  const openModal = (modal: ActiveModal) => {
    setActionError(null);
    setSelfieNote(null);
    setActiveModal(modal);
  };

  /** [Chụp lại] — v0 CHƯA tích hợp camera capture (không mock ảnh) */
  const handleRetake = () => {
    setSelfieNote("Camera chưa tích hợp trong bản v0 — bản ghi sẽ gửi không ảnh, ảnh selfie bổ sung ở bản sau");
  };

  /** CLOCK-ING → CLOCK-DONE: gọi check-in/out THẬT → toast "Đã chấm công" */
  const confirmClock = () => {
    if (geo.kind !== "ok") return;
    const payload = {
      latitude: geo.latitude,
      longitude: geo.longitude,
      accuracy: geo.accuracy,
      // v0: không photo_url — camera capture chưa tích hợp (xem handleRetake),
      // KHÔNG mock ảnh. Backend nhận photo_url optional.
      device_info: {
        source: "manager-profile",
        method: activeModal === "selfie" ? "selfie" : "gps",
        user_agent: navigator.userAgent,
      },
    };
    const mutation = clockDir === "VÀO" ? checkInMutation : checkOutMutation;
    mutation.mutate(payload, {
      onSuccess: () => {
        closeModal();
        setToast("Đã chấm công");
      },
      onError: (error) => {
        const detail =
          (error as { message?: string })?.message || "Chấm công không thành công. Thử lại";
        setActionError(detail);
      },
    });
  };

  /** [3] Đổi mật khẩu → modal [M] (reset state mỗi lần mở) */
  const openPwdModal = () => {
    setPwdCurrent("");
    setPwdNew("");
    setPwdConfirm("");
    setPwdNewError(false);
    setPwdConfirmError(false);
    setPwdBanner(null);
    setActiveModal("pwd");
  };

  /** PWD-VALIDATE: MK mới ≥8 ký tự + nhập lại khớp → POST change-password thật.
   *  PWD-SAI-MẬT-KHẨU-CŨ (server 400) → banner [Mb] "Mật khẩu hiện tại không đúng". */
  const submitPassword = () => {
    const newOk = pwdNew.length >= 8;
    const confirmOk = pwdConfirm === pwdNew && pwdConfirm.length > 0;
    setPwdNewError(!newOk);
    setPwdConfirmError(!confirmOk);
    if (!newOk || !confirmOk) return;
    setPwdBanner(null);
    changePasswordMutation.mutate(
      { old_password: pwdCurrent, new_password: pwdNew },
      {
        onSuccess: () => {
          closeModal();
          setToast("Đã đổi mật khẩu");
        },
        onError: (error) => {
          const err = error as { statusCode?: number; message?: string };
          const msg = err?.message ?? "";
          if (err?.statusCode === 400 || msg.toLowerCase().includes("old password")) {
            setPwdBanner("Mật khẩu hiện tại không đúng");
          } else {
            setPwdBanner(msg || "Đổi mật khẩu không thành công. Thử lại");
          }
        },
      },
    );
  };

  // ── Render helpers ──

  /** Hậu tố duyệt verbatim mockup: BM "· Đã ghi nhận" · khác "· Chờ duyệt"/"· Đã duyệt" */
  const approvalSuffix = (isApproved: boolean) =>
    isAutoRecorded ? "· Đã ghi nhận" : isApproved ? "· Đã duyệt" : "· Chờ duyệt";

  /** [4] dòng trạng thái hôm nay (verbatim mockup) */
  const renderClockStatus = () => {
    if (!statusData || clockState === "NOT_CLOCKED_IN") {
      return <span className={`${styles.st} ${styles.stNone}`}>Chưa chấm công hôm nay</span>;
    }
    const inTime = formatTime(statusData.last_clock_in);
    return (
      <span>
        <span className={`${styles.st} ${styles.stOk}`}>
          {clockState === "CLOCKED_IN"
            ? `● Đã vào ${inTime} · chưa chấm ra`
            : `● Đã vào ${inTime} · Đã ra ${formatTime(statusData.last_clock_out)}`}
        </span>{" "}
        <span className={styles.stNone}>{approvalSuffix(statusData.is_approved)}</span>
      </span>
    );
  };

  /** [4] dòng "Ca hôm nay: M6 · Ca sáng 07:00–17:00" (không ca → ẩn, profile-bm) */
  const renderTodayShift = () => {
    if (!todayShift) return null;
    const label = [todayShift.shift_code, todayShift.shift_name].filter(Boolean).join(" · ");
    return (
      <div className={styles.clockShift}>
        Ca hôm nay:{" "}
        <strong>
          {label ? `${label} ` : ""}
          {todayShift.start_time}–{todayShift.end_time}
        </strong>
      </div>
    );
  };

  /** [6] 1 dòng lịch sử — có ca / không ca (BM) / nghỉ theo lịch */
  const renderHistoryRecord = (day: Date, record: SelfAttendanceRecord) => {
    const dateLabel = formatDayMonth(day);
    const inTime = formatTime(record.clock_in);
    const outTime = record.clock_out ? formatTime(record.clock_out) : null;
    if (record.shift_code || record.scheduled_start) {
      // Có ca: "M6 · 06:58–17:03 ● Đúng giờ · Đã duyệt"
      const shiftLabel = record.shift_code ?? `${record.scheduled_start}–${record.scheduled_end}`;
      return (
        <div className={styles.historyRow} key={record.id}>
          <span className="d">{dateLabel}</span> {shiftLabel} · {inTime}–{outTime ?? "—:—"}{" "}
          {record.clock_out == null ? (
            <span className={`${styles.st} ${styles.stNone}`}>· Chưa chấm ra</span>
          ) : (
            <span className={`${styles.st} ${record.is_late ? styles.stLate : styles.stOk}`}>
              {record.is_late ? "▲ Đi trễ" : "● Đúng giờ"}
              <span className={styles.stNone}>&nbsp;{approvalSuffix(record.is_approved)}</span>
            </span>
          )}
        </div>
      );
    }
    // Không ca (BM ghi nhận tự do): "Vào 08:00 · Ra 17:30 ● Đã ghi nhận"
    return (
      <div className={styles.historyRow} key={record.id}>
        <span className="d">{dateLabel}</span> Vào {inTime}
        {outTime ? ` · Ra ${outTime}` : " · chưa chấm ra"}{" "}
        <span className={`${styles.st} ${styles.stOk}`}>
          ● {isAutoRecorded ? "Đã ghi nhận" : record.is_approved ? "Đã duyệt" : "Chờ duyệt"}
        </span>
      </div>
    );
  };

  /** [6] Gần đây — 6 ngày trước hôm nay (mockup ~5 ngày, không lặp hôm nay ở [4]) */
  const renderHistory = () => {
    if (historyQuery.isLoading || myScheduleQuery.isLoading) {
      return (
        <div aria-busy="true">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className={`${styles.skeleton} ${styles.skeletonRow}`} />
          ))}
        </div>
      );
    }
    // F-1: 404 (chưa có employee record) → rơi xuống empty-state "Chưa có dữ liệu"
    if (historyQuery.isError && !isNotFound(historyQuery)) {
      return (
        <div className={`${styles.banner} ${styles.bannerError}`} role="alert">
          Không tải được lịch sử. Thử lại
          <button
            type="button"
            className={`${styles.btn} ${styles.btnSecondary} ${styles.btnSm}`}
            style={{ marginLeft: "auto" }}
            onClick={() => historyQuery.refetch()}
          >
            Thử lại
          </button>
        </div>
      );
    }

    const records = historyQuery.data?.records ?? [];
    // Map ngày local → bản ghi đầu tiên trong ngày (BE trả desc theo clock_in)
    const recordByDate = new Map<string, SelfAttendanceRecord>();
    records.forEach((r) => {
      const key = toISODate(new Date(r.clock_in));
      if (!recordByDate.has(key)) recordByDate.set(key, r);
    });
    // Ngày có ca trong tuần (my-schedule this_week) — nhận diện "nghỉ theo lịch"
    const scheduledDates = new Set((myScheduleQuery.data?.this_week ?? []).map((s) => s.date));
    const scheduleCovered = myScheduleQuery.data != null;

    const rows: React.ReactNode[] = [];
    for (let i = 1; i <= 6; i++) {
      const day = new Date();
      day.setDate(day.getDate() - i);
      const key = toISODate(day);
      const record = recordByDate.get(key);
      if (record) {
        rows.push(renderHistoryRecord(day, record));
      } else if (scheduleCovered && !scheduledDates.has(key)) {
        // Chắc chắn không có ca (my-schedule phủ tuần này) → nghỉ theo lịch
        rows.push(
          <div className={styles.historyRow} key={key}>
            <span className="d">{formatDayMonth(day)}</span>{" "}
            <span className={styles.stNone}>— (nghỉ theo lịch)</span>
          </div>,
        );
      }
      // Ngày ngoài phạm vi my-schedule mà không có bản ghi → bỏ qua (không suy diễn)
    }

    if (rows.length === 0) {
      return <div className={styles.emptyState}>Chưa có dữ liệu chấm công gần đây.</div>;
    }
    return <>{rows}</>;
  };

  /** Dòng vị trí trong modal [C1]/[C2] — ngoài bán kính vẫn cho xác nhận (v0) */
  const renderGeoLine = (prefix: string) => {
    if (geo.kind === "loading") {
      return (
        <div className={styles.gpsLine}>
          <span className={`${styles.spinner} ${styles.spinnerDark}`} aria-hidden="true" /> Đang
          lấy vị trí…
        </div>
      );
    }
    if (geo.kind === "error") {
      return (
        <div className={styles.gpsLine}>
          <span className={`${styles.st} ${styles.stBad}`}>✖ {geo.message}</span>
        </div>
      );
    }
    const distanceText =
      geoDistance != null
        ? geoDistance <= gpsRadius
          ? `● Trong bán kính ${gpsRadius}m ✔ (lệch ${Math.round(geoDistance)}m)`
          : `▲ Ngoài bán kính ${gpsRadius}m (lệch ${Math.round(geoDistance)}m) — vẫn được ghi nhận, đánh dấu GPS lệch`
        : "Chưa có tọa độ chi nhánh để đối chiếu";
    const distanceCls =
      geoDistance != null
        ? geoDistance <= gpsRadius
          ? styles.stOk
          : styles.stLate
        : styles.stNone;
    return (
      <div className={styles.gpsLine}>
        <div>
          {prefix}{" "}
          <span className={`${styles.st} ${distanceCls}`}>{distanceText}</span>
        </div>
        <div className={`${styles.muted} ${styles.txtSm}`} style={{ marginTop: "var(--nqh-space-1)" }}>
          {geo.latitude.toFixed(2)}°N, {geo.longitude.toFixed(2)}°E · {formatTime(geo.at.toISOString())}
        </div>
      </div>
    );
  };

  // ══════════════════════════════ RENDER ══════════════════════════════

  const profile = profileQuery.data;
  const branchLabel = profile?.branch_code || profile?.branch_name || "";
  // "Phụ trách: BKL · Pha chế" (có khu vực) · "BKL (Nhân viên Buôn Kơ Lang)" (BM — profile-bm)
  // VĐ7 (2026-08-16): đổi "mọi khu vực" → "Nhân viên [chi nhánh phụ trách]" —
  // dùng branch_name đầy đủ (The Kupid, Thơm, Buôn Kơ Lang, Trại Mèo Mướp,
  // AirDream) thay vì mã chi nhánh viết tắt cho phần trong ngoặc.
  const chargeLabel = profile
    ? profile.department_name
      ? [branchLabel, profile.department_name].filter(Boolean).join(" · ")
      : `${branchLabel} (Nhân viên ${profile.branch_name || branchLabel})`
    : "—";

  const isPageLoading = profileQuery.isLoading || statusQuery.isLoading;
  // F-1 fix (2026-08-04, findings preflight): 404 "Employee record not found" là
  // TRẠNG THÁI HỢP LỆ (tenant mới / user chưa có employee record) — render với
  // fallback từ auth store + empty-state, KHÔNG phải lỗi trang. 5xx/network vẫn banner.
  const isNotFound = (q: { isError: boolean; error: unknown }): boolean => {
    if (!q.isError) return false;
    const e = q.error as { statusCode?: number; response?: { status?: number } } | null;
    return (e?.statusCode ?? e?.response?.status) === 404;
  };
  const isPageError =
    (profileQuery.isError && !isNotFound(profileQuery)) ||
    (statusQuery.isError && !isNotFound(statusQuery));
  const clockBusy = checkInMutation.isPending || checkOutMutation.isPending;
  const now = new Date();

  return (
    <>
      {/* [1] Tiêu đề vùng — không nút primary cấp trang */}
      <div className={styles.pageHead}>
        <h1 className={styles.pageHeadTitle}>Thông tin của tôi</h1>
      </div>

      {isPageLoading ? (
        <div aria-busy="true">
          <div className={`${styles.skeleton} ${styles.skeletonCard}`} />
          <div className={`${styles.skeleton} ${styles.skeletonCard}`} />
        </div>
      ) : isPageError ? (
        <div className={`${styles.banner} ${styles.bannerError}`} role="alert">
          Không tải được dữ liệu. Thử lại
          <button
            type="button"
            className={`${styles.btn} ${styles.btnSecondary} ${styles.btnSm}`}
            style={{ marginLeft: "auto" }}
            onClick={() => {
              profileQuery.refetch();
              statusQuery.refetch();
            }}
          >
            Thử lại
          </button>
        </div>
      ) : (
        <>
          {/* ══════════ [2] Khối HỒ SƠ (read) ══════════ */}
          <section className={styles.profCard} aria-labelledby="profTitle">
            <div className={styles.profCardTitle} id="profTitle">Hồ sơ</div>
            <div className={styles.profGrid}>
              <div>Họ tên: <strong>{profile?.full_name ?? user?.full_name ?? "—"}</strong></div>
              <div>Vai trò: <strong>{roleLabel}</strong></div>
              <div>Phụ trách: <strong>{chargeLabel}</strong></div>
              <div>Điện thoại: <strong>{profile?.phone_number ?? "—"}</strong></div>
              <div>Email: <strong>{profile?.email ?? "—"}</strong></div>
              <div>Tên đăng nhập: <strong>{profile?.username ?? "—"}</strong></div>
            </div>
            <div className={styles.profFoot}>
              {/* [3] Đổi mật khẩu → modal [M] (self-đổi, cần MK hiện tại) */}
              <button type="button" className={`${styles.btn} ${styles.btnSecondary}`} onClick={openPwdModal}>
                Đổi mật khẩu
              </button>
            </div>
          </section>

          {/* ══════════ [4] Khối CHẤM CÔNG CỦA TÔI ══════════ */}
          <section className={styles.profCard} aria-labelledby="clockTitle">
            <div className={styles.profCardTitle} id="clockTitle">
              Chấm công của tôi
              <span className={styles.profCardSub}>· Hôm nay {formatDateVi(now)}</span>
            </div>

            {/* Có ca hôm nay → hiện dòng ca (không ca → ẩn, profile-bm.html) */}
            {renderTodayShift()}

            <div className={styles.clockStatus}>
              <span>Trạng thái</span>
              {renderClockStatus()}
            </div>

            {/* [5] Nút Chấm công vào/ra — nhãn động (đã ra → ẩn) */}
            {showClockButton && (
              <div className={styles.clockCta}>
                <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} onClick={openMethodPicker}>
                  <CameraIcon size={18} />
                  <span>Chấm công {clockDir}</span>
                </button>
              </div>
            )}

            {/* [6] Lịch sử gần đây (chỉ đọc) */}
            <div className={styles.history}>
              <div className={styles.historyTitle}>Gần đây</div>
              {renderHistory()}
            </div>
          </section>
        </>
      )}

      {/* ══════════ [P] POPUP "Chọn phương thức chấm công" — giữa màn ~360px ══════════
          Chỉ hiện phương thức được bật ở chi nhánh (SC-004 → GET /clock-config) */}
      {activeModal === "method" && (
        <div className={styles.modalOverlay}>
          <div className={`${styles.modal} ${styles.modalMethod}`} role="dialog" aria-modal="true" aria-labelledby="methodTitle">
            <div className={styles.modalHead}>
              <h2 className={styles.modalTitle} id="methodTitle">Chọn phương thức chấm công</h2>
              <button type="button" className={styles.btnIcon} aria-label="Đóng" onClick={closeModal}>
                <CloseIcon />
              </button>
            </div>
            <div className={styles.modalBody}>
              {clockConfigQuery.isLoading ? (
                <div aria-busy="true">
                  <div className={`${styles.skeleton} ${styles.skeletonRow}`} />
                  <div className={`${styles.skeleton} ${styles.skeletonRow}`} />
                </div>
              ) : (
                <>
                  {/* ① Chụp ảnh selfie (khuyến nghị — xác thực mạnh hơn) */}
                  {clockConfig?.selfie_enabled && (
                    <button type="button" className={styles.methodItem} onClick={() => openModal("selfie")}>
                      <span>
                        <span className={styles.methodItemName}>
                          <CameraIcon />
                          Chụp ảnh selfie
                        </span>
                        <span className={styles.methodItemSub}>Ảnh có GPS + timemark (khuyến nghị)</span>
                      </span>
                      <ChevronRightIcon />
                    </button>
                  )}
                  {/* ② Định vị GPS (không ảnh) */}
                  {clockConfig?.gps_enabled && (
                    <button type="button" className={styles.methodItem} onClick={() => openModal("gps")}>
                      <span>
                        <span className={styles.methodItemName}>
                          <GpsIcon />
                          Định vị GPS
                        </span>
                        <span className={styles.methodItemSub}>Gửi vị trí, không ảnh</span>
                      </span>
                      <ChevronRightIcon />
                    </button>
                  )}
                  {/* ③ Chấm công thủ công (Timemark) — SC-013-MGR-PROFILE popup
                      [P] annotation: "LUÔN hiện (không phụ thuộc cấu hình
                      SC-004) — escape hatch khi selfie/GPS đều không dùng
                      được." Do đó, KHÔNG gate theo clockConfig ở đây (khác
                      với ① và ②) — đúng nghĩa "phương thức cuối cùng" khi
                      chi nhánh tắt cả 2 phương thức kia. */}
                  <button type="button" className={styles.methodItem} onClick={() => openModal("timemark")}>
                    <span>
                      <span className={styles.methodItemName}>
                        <ClockIconTimemark />
                        Chấm công thủ công (Timemark)
                      </span>
                      <span className={styles.methodItemSub}>Cần Quản lý chi nhánh phê duyệt</span>
                    </span>
                    <ChevronRightIcon />
                  </button>
                  {clockConfig && !clockConfig.selfie_enabled && !clockConfig.gps_enabled && (
                    <div className={styles.emptyState}>
                      Chi nhánh chưa bật phương thức chấm công tự động — dùng Timemark bên dưới (cần phê duyệt).
                    </div>
                  )}
                </>
              )}
            </div>
            <div className={styles.modalFoot}>
              <button type="button" className={`${styles.btn} ${styles.btnSecondary}`} onClick={closeModal}>
                Hủy
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══════════ [C3] MODAL "Chấm công thủ công (Timemark)" — SC-013-MGR-PROFILE
          ③, tái dùng TimemarkForm.tsx nguyên trạng (component tự chứa: state
          lý do/submit/success, gọi thẳng attendanceApi.submitTimemark). Modal
          shell theo đúng pattern [C1]/[C2] (overlay + card), nhưng nội dung
          form giao hoàn toàn cho TimemarkForm — không tự vẽ lại UI. ══════════ */}
      {activeModal === "timemark" && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal} role="dialog" aria-modal="true" aria-label="Chấm công thủ công (Timemark)">
            <TimemarkForm
              // bot-060 follow-up (GH#871, 2026-09-16): toast phải hiện NGAY
              // khi API success, không đợi user bấm "Về trang chủ" — dùng
              // onSubmitted (chạy tại thời điểm API success) thay vì
              // onSuccess (chỉ chạy khi user bấm nút đóng modal).
              onSubmitted={() => {
                setToast("Đã gửi yêu cầu Timemark — chờ Quản lý chi nhánh phê duyệt");
              }}
              onSuccess={() => {
                closeModal();
              }}
              onCancel={closeModal}
            />
          </div>
        </div>
      )}

      {/* ══════════ [C1] MODAL "Chấm công VÀO/RA · Selfie" — giữa màn ~480px ══════════ */}
      {activeModal === "selfie" && (
        <div className={styles.modalOverlay}>
          <div className={`${styles.modal} ${styles.modalSelfie}`} role="dialog" aria-modal="true" aria-labelledby="selfieTitle">
            <div className={styles.modalHead}>
              <h2 className={styles.modalTitle} id="selfieTitle">
                Chấm công {clockDir} · Selfie · {formatDayTime(now)}
              </h2>
              <button type="button" className={styles.btnIcon} aria-label="Đóng" onClick={closeModal}>
                <CloseIcon />
              </button>
            </div>
            <div className={styles.modalBody}>
              {actionError && (
                <div className={`${styles.banner} ${styles.bannerError}`} role="alert" style={{ marginBottom: "var(--nqh-space-3)" }}>
                  {actionError}
                </div>
              )}
              {/* Khung ảnh placeholder CÓ CẤU TRÚC — v0 CHƯA tích hợp camera
                  capture (không mock ảnh): xác nhận gửi không photo_url,
                  ảnh + GPS + timemark bổ sung ở bản sau (xem handleRetake) */}
              <div className={styles.selfieBox}>
                <CameraIcon size={32} strokeWidth={1.5} />
                <span>ảnh + GPS + timemark</span>
                <span>
                  {formatDayTime(now)}
                  {branchLabel ? ` · ${branchLabel}` : ""}
                </span>
                <span>(chưa chụp — camera tích hợp bản sau)</span>
                <button
                  type="button"
                  className={`${styles.btn} ${styles.btnSecondary}`}
                  style={{ minHeight: 36, padding: "4px 12px", marginTop: "var(--nqh-space-2)" }}
                  onClick={handleRetake}
                >
                  Chụp lại
                </button>
                {selfieNote && <span>{selfieNote}</span>}
              </div>
              {/* Vị trí (từ ảnh) — ngoài bán kính → cảnh báo nhưng VẪN cho xác nhận */}
              {renderGeoLine("Vị trí (từ ảnh):")}
            </div>
            <div className={`${styles.modalFoot} ${styles.modalFootSpace}`}>
              <button type="button" className={`${styles.btn} ${styles.btnSecondary}`} onClick={() => openModal("method")}>
                ‹ Đổi phương thức
              </button>
              <div className={styles.modalFootGroup}>
                <button type="button" className={`${styles.btn} ${styles.btnSecondary}`} onClick={closeModal}>
                  Hủy
                </button>
                <button
                  type="button"
                  className={`${styles.btn} ${styles.btnPrimary}`}
                  disabled={geo.kind !== "ok" || clockBusy}
                  onClick={confirmClock}
                >
                  {clockBusy && <span className={styles.spinner} aria-hidden="true" />}
                  <span>Xác nhận {clockDir}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══════════ [C2] MODAL "Chấm công VÀO/RA · Định vị GPS" — giữa màn ~440px ══════════ */}
      {activeModal === "gps" && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal} role="dialog" aria-modal="true" aria-labelledby="gpsTitle">
            <div className={styles.modalHead}>
              <h2 className={styles.modalTitle} id="gpsTitle">
                Chấm công {clockDir} · Định vị GPS · {formatDayTime(now)}
              </h2>
              <button type="button" className={styles.btnIcon} aria-label="Đóng" onClick={closeModal}>
                <CloseIcon />
              </button>
            </div>
            <div className={styles.modalBody}>
              {actionError && (
                <div className={`${styles.banner} ${styles.bannerError}`} role="alert" style={{ marginBottom: "var(--nqh-space-3)" }}>
                  {actionError}
                </div>
              )}
              {/* GPS đang lấy → spinner; ngoài bán kính → cảnh báo nhưng VẪN
                  cho xác nhận (đánh dấu GPS lệch, v0 không chặn) */}
              {renderGeoLine("Vị trí GPS:")}
              <div className={`${styles.muted} ${styles.txtSm}`}>(không có ảnh selfie)</div>
            </div>
            <div className={`${styles.modalFoot} ${styles.modalFootSpace}`}>
              <button type="button" className={`${styles.btn} ${styles.btnSecondary}`} onClick={() => openModal("method")}>
                ‹ Đổi phương thức
              </button>
              <div className={styles.modalFootGroup}>
                <button type="button" className={`${styles.btn} ${styles.btnSecondary}`} onClick={closeModal}>
                  Hủy
                </button>
                <button
                  type="button"
                  className={`${styles.btn} ${styles.btnPrimary}`}
                  disabled={geo.kind !== "ok" || clockBusy}
                  onClick={confirmClock}
                >
                  {clockBusy && <span className={styles.spinner} aria-hidden="true" />}
                  <span>Xác nhận {clockDir}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══════════ [M] MODAL "Đổi mật khẩu" — self-đổi, cần mật khẩu hiện tại ══════════ */}
      {activeModal === "pwd" && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal} role="dialog" aria-modal="true" aria-labelledby="pwdTitle">
            <div className={styles.modalHead}>
              <h2 className={styles.modalTitle} id="pwdTitle">Đổi mật khẩu</h2>
              <button type="button" className={styles.btnIcon} aria-label="Đóng" onClick={closeModal}>
                <CloseIcon />
              </button>
            </div>
            <div className={styles.modalBody}>
              {/* [Mb] banner lỗi — PWD-SAI-MẬT-KHẨU-CŨ → "Mật khẩu hiện tại không đúng" */}
              {pwdBanner && (
                <div className={`${styles.banner} ${styles.bannerError}`} role="alert" style={{ marginBottom: "var(--nqh-space-3)" }}>
                  <span className="ico" aria-hidden="true"><WarningIcon /></span>
                  <span>{pwdBanner}</span>
                </div>
              )}
              <div className={styles.field}>
                <label htmlFor="pwdCurrent">Mật khẩu hiện tại *</label>
                <input
                  id="pwdCurrent"
                  className={styles.input}
                  type="password"
                  autoComplete="current-password"
                  value={pwdCurrent}
                  onChange={(e) => setPwdCurrent(e.target.value)}
                />
              </div>
              <div className={styles.field}>
                <label htmlFor="pwdNew">Mật khẩu mới *</label>
                <input
                  id="pwdNew"
                  className={`${styles.input} ${pwdNewError ? styles.inputError : ""}`}
                  type="password"
                  autoComplete="new-password"
                  value={pwdNew}
                  onChange={(e) => setPwdNew(e.target.value)}
                />
                <div className={`${styles.fieldError} ${pwdNewError ? styles.fieldErrorShow : ""}`}>
                  Mật khẩu tối thiểu 8 ký tự
                </div>
              </div>
              <div className={styles.field}>
                <label htmlFor="pwdConfirm">Nhập lại mật khẩu mới *</label>
                <input
                  id="pwdConfirm"
                  className={`${styles.input} ${pwdConfirmError ? styles.inputError : ""}`}
                  type="password"
                  autoComplete="new-password"
                  value={pwdConfirm}
                  onChange={(e) => setPwdConfirm(e.target.value)}
                />
                <div className={`${styles.fieldError} ${pwdConfirmError ? styles.fieldErrorShow : ""}`}>
                  Nhập lại không khớp
                </div>
              </div>
            </div>
            <div className={styles.modalFoot}>
              <button type="button" className={`${styles.btn} ${styles.btnSecondary}`} onClick={closeModal}>
                Hủy
              </button>
              <button
                type="button"
                className={`${styles.btn} ${styles.btnPrimary}`}
                disabled={changePasswordMutation.isPending}
                onClick={submitPassword}
              >
                {changePasswordMutation.isPending && <span className={styles.spinner} aria-hidden="true" />}
                <span>Đổi mật khẩu</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast (pattern window.nqhToast) */}
      {toast && (
        <div className={styles.toast} role="status">
          {toast}
        </div>
      )}
    </>
  );
};

export default ManagerProfilePage;
